import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare } from 'lucide-react';
import { useTeamChat } from '../store/TeamChatContext';
import { teamService } from '../../../services/team/TeamService';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../components/ui/utils';

function label(peerId: string): string {
  const m = teamService.getMember(peerId);
  if (!m) return peerId;
  return `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`.trim() || m.email.slice(0, 2);
}

export function TeamChatDock() {
  const { floating, restoreDockedChat, closeFloatingChat, teammates } = useTeamChat();

  const docked = useMemo(() => floating.filter((w) => w.presentation === 'docked'), [floating]);

  const unreadFor = (peerId: string) => {
    const row = teammates.find((t) => t.member.id === peerId);
    return row?.unread ?? 0;
  };

  if (typeof document === 'undefined' || docked.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[10055] flex justify-center pb-3 px-3">
      <motion.div
        layout
        className="pointer-events-auto flex max-w-full items-end gap-2 rounded-2xl border border-card-border/50 bg-card-bg/90 px-2 py-2 shadow-2xl shadow-black/35 ring-1 ring-cyber-cyan/10 backdrop-blur-xl transition-shadow duration-300 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]"
      >
        <div className="flex items-center gap-1 border-r border-card-border/40 pr-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5 text-cyber-cyan" />
          Chats
        </div>
        <AnimatePresence initial={false}>
          {docked.map((w) => {
            const u = unreadFor(w.peerId);
            return (
              <motion.div
                key={w.peerId}
                layout
                initial={{ opacity: 0, y: 12, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.92 }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="flex items-center gap-1"
              >
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => restoreDockedChat(w.peerId)}
                  title="Restore chat"
                  className={cn(
                    'relative flex h-11 w-11 items-center justify-center rounded-xl text-xs font-bold text-white',
                    'bg-gradient-to-br from-cyber-cyan/45 to-cyber-blue/50 ring-1 ring-card-border/40 shadow-md',
                    'hover:ring-cyber-cyan/35 hover:shadow-cyber-cyan/15 transition-shadow',
                  )}
                >
                  {label(w.peerId)}
                  {u > 0 ? (
                    <Badge className="absolute -top-1 -right-1 h-4 min-w-[1rem] px-0.5 text-[9px] border-0 bg-rose-500 shadow animate-pulse">
                      {u > 9 ? '9+' : u}
                    </Badge>
                  ) : null}
                </motion.button>
                <button
                  type="button"
                  className="h-7 w-7 rounded-lg text-xs text-muted-foreground hover:text-white hover:bg-white/10 border border-border/50"
                  onClick={() => closeFloatingChat(w.peerId)}
                  title="Close"
                >
                  ×
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </div>,
    document.body,
  );
}
